import React, { type ReactNode, useCallback, useContext, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Spotlight as SpotlightImpl, SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';

import { useEditionAwareness } from '../services/edition-awareness-context';

import { EDITION_AWARENESS_SPOTLIGHT_TARGET } from './edition-awareness-button';

type ActiveSpotlightId = 'onboarding-available-after-dismiss' | 'standard-to-premium';

const SpotlightContext = React.createContext<((id: ActiveSpotlightId) => void) | null>(null);

export function useSpotlight() {
	const setActiveSpotlight = useContext(SpotlightContext);

	if (!setActiveSpotlight) {
		if (process.env.NODE_ENV !== 'production' && !process.env.CI) {
			throw new Error('useSpotlight needs to be used within the SpotlightContext');
		}

		return { setActiveSpotlight: () => {} };
	}

	return { setActiveSpotlight };
}

function GotIt() {
	return (
		<FormattedMessage
			id="@atlassian/edition-awareness.spotlights.got-it"
			defaultMessage="Got it"
			description="Spotlight button text which is used to dismiss the spotlight"
		/>
	);
}

function ActiveSpotlight({ id, onDismiss }: { id: ActiveSpotlightId; onDismiss: () => void }) {
	const { product } = useEditionAwareness();

	if (id === 'onboarding-available-after-dismiss') {
		return (
			<SpotlightTransition>
				<SpotlightImpl
					actions={[
						{
							onClick: onDismiss,
							text: <GotIt />,
						},
					]}
					target={EDITION_AWARENESS_SPOTLIGHT_TARGET}
					targetRadius={3}
					dialogWidth={275}
				>
					<FormattedMessage
						id="@atlassian/edition-awareness.spotlights.onboarding-available-after-dismiss"
						defaultMessage="Your new features are right up here, if you want to see them later."
						description="Spotlight message which helps the customer know they can come back to the modal they just dismissed by clicking the spotlighted item"
					/>
				</SpotlightImpl>
			</SpotlightTransition>
		);
	}

	if (id === 'standard-to-premium') {
		const target = product === 'confluence' ? 'nav-profile' : 'profile-spotlight';

		return (
			<SpotlightImpl
				actions={[
					{
						onClick: onDismiss,
						text: <GotIt />,
					},
				]}
				target={target}
				targetRadius={3}
				dialogWidth={275}
				targetBgColor={token('utility.elevation.surface.current')}
			>
				<FormattedMessage
					id="@atlassian/edition-awareness.spotlight.message"
					defaultMessage="Not ready to upgrade? Return to your profile when you're ready."
					description="Message to show in the spotlight on the profile menu icon when the user dismisses the EA pill and is eligible for standard to premium upsell"
				/>
			</SpotlightImpl>
		);
	}

	return null;
}

export function Spotlights({ children }: { children: ReactNode }) {
	const [activeSpotlight, setActiveSpotlight] = useState<ActiveSpotlightId | null>(null);
	const handleOnDismiss = useCallback(() => setActiveSpotlight(null), []);

	return (
		<>
			<SpotlightContext.Provider value={setActiveSpotlight}>{children}</SpotlightContext.Provider>
			{activeSpotlight && <ActiveSpotlight id={activeSpotlight} onDismiss={handleOnDismiss} />}
		</>
	);
}
